/**
 * Writes a tiny JSON snapshot to a shared App Group container so the iOS
 * WidgetKit extension can read it without a network call.
 *
 * On web (PWA), this is a no-op. On iOS (Capacitor), it calls the native
 * `MyFlowHabitWidgetBridge` plugin which writes to
 * `UserDefaults(suiteName: "group.com.drbrandtgibson.myflowhabit")`.
 *
 * Snapshot shape (kept lean — widgets re-render every ~30 min):
 *   {
 *     updatedAt: ISO string,
 *     done: number,
 *     total: number,
 *     dateISO: string,                // user's local date
 *     loggedOut: boolean,
 *     items: [{ id, name, icon, color, time_of_day, completed }]
 *   }
 */
import { Capacitor } from '@capacitor/core';

const isNative = () => {
  try { return Capacitor?.isNativePlatform?.() === true; } catch { return false; }
};

// Used by the Settings → Widget diagnostic panel.
export const getWidgetBridgeStatus = () => {
  if (!isNative()) return { available: false, reason: 'web' };
  const plugin = Capacitor?.Plugins?.MyFlowHabitWidget;
  if (!plugin) return { available: false, reason: 'plugin-not-registered' };
  return { available: true, plugin };
};

/**
 * Returns a structured result so callers can toast / log success or error.
 * { ok: true, wrote: N } on success.
 * { ok: false, reason: 'web' | 'plugin-not-registered' | string } on failure.
 */
export const updateWidgetSnapshot = async (snapshot) => {
  const status = getWidgetBridgeStatus();
  if (!status.available) {
    return { ok: false, reason: status.reason };
  }
  try {
    const res = await status.plugin.setSnapshot({ snapshot: JSON.stringify(snapshot) });
    await status.plugin.reload?.();
    return { ok: true, wrote: res?.wrote };
  } catch (err) {
    const reason = (err && (err.message || err.errorMessage)) || String(err);
    console.warn('[mfh] widget update failed:', reason);
    return { ok: false, reason };
  }
};

export const setWidgetLoggedOut = async () => {
  return updateWidgetSnapshot({
    updatedAt: new Date().toISOString(),
    loggedOut: true,
    done: 0,
    total: 0,
    items: [],
  });
};

export const diagnoseWidget = async () => {
  const status = getWidgetBridgeStatus();
  if (!status.available) return { available: false, reason: status.reason };
  try {
    const diag = await status.plugin.diagnose();
    return { available: true, ...diag };
  } catch (err) {
    return { available: true, error: err?.message || String(err) };
  }
};

export const readWidgetSnapshot = async () => {
  const status = getWidgetBridgeStatus();
  if (!status.available) return null;
  try {
    const { snapshot } = await status.plugin.getSnapshot();
    if (!snapshot) return null;
    return JSON.parse(snapshot);
  } catch {
    return null;
  }
};
